import type { NotificationKind, TrayNotificationData } from "./notificationTray";

const envPreview = import.meta.env.VITE_NOTIFICATION_PREVIEW as string | undefined;

/**
 * Pinned sample tray for UI work: no auto-dismiss; primary link does not navigate.
 * - In **dev**, on by default. Set `VITE_NOTIFICATION_PREVIEW=false` in `.env` to use real notifications.
 * - In **production**, off unless `VITE_NOTIFICATION_PREVIEW=true`.
 */
/** Vitest sets `import.meta.env.VITEST`; keep preview off so tests see normal tray behavior. */
export const ENABLE_NOTIFICATION_PREVIEW =
  !import.meta.env.VITEST &&
  (envPreview === "true" || (import.meta.env.DEV && envPreview !== "false"));

/**
 * Which `notification_kind` sample to show while preview is on. Change this to
 * try alert / info / success styling: e.g. `shift_updated`, `shift_created`,
 * `patrol_upcoming`, `shift_cancelled`.
 */
export const NOTIFICATION_PREVIEW_KIND: NotificationKind = "patrol_upcoming";

const PREVIEW_COPY: Record<
  NotificationKind,
  { title: string; body: string; category: "patrol" | "shift" }
> = {
  patrol_overdue: {
    category: "patrol",
    title: "Patrol overdue",
    body: "Checkpoint A is overdue. Scan the nearest patrol QR code now.",
  },
  patrol_upcoming: {
    category: "patrol",
    title: "Patrol due soon",
    body: "Next checkpoint is due in 5 minutes.",
  },
  shift_created: {
    category: "shift",
    title: "New shift",
    body: "You have been assigned a shift on Saturday 14:00–22:00 at East Gate.",
  },
  shift_updated: {
    category: "shift",
    title: "Shift updated",
    body: "Your shift is now 15:00–23:00. Review before clock-in.",
  },
  shift_cancelled: {
    category: "shift",
    title: "Shift cancelled",
    body: "Your Sunday shift has been cancelled.",
  },
  shift_deleted: {
    category: "shift",
    title: "Shift removed",
    body: "A scheduled shift was removed from your calendar.",
  },
  shift_copied: {
    category: "shift",
    title: "Shift copied",
    body: "A shift was duplicated to next week.",
  },
  shift_reassigned_away: {
    category: "shift",
    title: "Shift reassigned",
    body: "You are no longer assigned to the Tuesday evening shift.",
  },
};

export function getPreviewTray(kind: NotificationKind): {
  title: string;
  body: string;
  data: TrayNotificationData;
} {
  const copy = PREVIEW_COPY[kind];
  return {
    title: copy.title,
    body: copy.body,
    data: {
      notification_category: copy.category,
      notification_kind: kind,
      title: copy.title,
      body: copy.body,
    },
  };
}
